import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Types
import { StepTasks } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.types';
// Models
import { RootState } from 'app/store';
import IOption from 'app/models/Option';
// Store
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectCollectionsAsOptionsByFileType_TODO } from 'app/store/DMSCollections/DMSCollections.selectors';
// Box
import { Box, Divider, MenuItem, TextField, Typography } from '@mui/material';
// Icons
import { Add as AddIcon } from '@mui/icons-material';
// Dialogs
import CollectionFormDialog from 'app/pages/Admin/CollectionsPage/CollectionFormDialog';
// Components
import LabelsForm from 'app/components/LabelsForm';
import { Button, Input } from 'app/components/Mui';
import { OutlineBlock } from 'app/components/Utilities';
// Utilities
import { isRequired } from 'app/utilities/Validations';
// 
import { useWorkflowContext } from '../Workflow.context'

const CreateDocumentControls:FC = () => {
  const { t } = useTranslation();

  const { selectedStep, getStepFieldIndex } = useWorkflowContext();

  // State
  const collectionOptions = useAppSelector((state:RootState) => selectCollectionsAsOptionsByFileType_TODO(state, {
    fileFormat: 'custom'
  })) || [];

  const { control, formState:{ errors } } = useFormContext();

  const { open, toggle } = useToggle();

  if ( !selectedStep || selectedStep.task !== StepTasks.CreateDocument ) return null;

  const fieldIndex = getStepFieldIndex(selectedStep.id) as number;

  const collectionIdError = (errors as any)?.steps?.[fieldIndex]?.input?.parameters?.collectionId;

  return (
    <Fragment>
      {open ? (
        <CollectionFormDialog
          open={open}
          onClose={toggle}
        />
      ) : null}
      
      <Controller
        control={control}
        name={`steps[${fieldIndex}].input.parameters.name`}
        render={({ field }) => (
          <Input {...field} label={t('labels.name')} size="small" />
        )}
      />
      <Controller
        control={control}
        name={`steps[${fieldIndex}].input.parameters.collectionId`}
        rules={{ required: isRequired }}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            size="small"
            label={t('labels.documentCollection')}
            required
            error={Boolean(collectionIdError)}
            helperText={collectionIdError?.message || ''}
            slotProps={{
              select: {
                renderValue: (value) => {
                  if ( !value ) return '';
                  const foundedOption = collectionOptions.find((option) => option.id.toString() === value.toString());
                  return foundedOption?.name || '';
                }
              }
            }}
          >
            {collectionOptions.map((option:IOption, index:number) => (
              <MenuItem
                sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                key={`option item ${index} ${option.id}`}
                value={option.id.toString()}
              >
                <Typography variant="subtitle1">{option.name}</Typography>
                <Typography sx={{ maxWidth: 320, whiteSpace: 'normal' }} variant="caption">{option.props.description}</Typography>
              </MenuItem>
            ))}
            <Divider />
            <Box sx={{ px: 4 }}>
              <Button
                name="create-document-collection-button"
                fullWidth
                onClick={toggle}
                startIcon={<AddIcon />}
              >{t('buttons.addCollection')}</Button>
            </Box>
          </TextField>
        )}
      />
      <OutlineBlock sx={{ pt: 6, mt: 6 }} label={t('labels.labels')}>
        <Fragment>
          <Controller
            control={control} defaultValue={[]}
            name={`steps[${fieldIndex}].input.parameters.labels`}
            render={({ field }) => (
              <Fragment>
                <LabelsForm
                  labels={field.value}
                  onUpdate={(newLabels) => field.onChange(newLabels)}
                  size="small"
                />
              </Fragment>
            )}
          />
        </Fragment>
      </OutlineBlock>
    </Fragment>
  )
}

export default CreateDocumentControls;